import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Router } from '@angular/router';
import { UrlEnum } from '@enums/url.enum';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { UrlService } from '../url/url.service';
import { BookedOrder, BookedOrderActionInfo, BookedOrderFlightBookingActionInfo } from './_interfaces_/booked-order.interface';
import { BookedOrderActionInfoMapped, BookedOrderMapped, LinksMapped } from './_interfaces_/booked-order.mapped.interface';
import { DocumentQueueResponse } from './_interfaces_/documents.interface';
import { OrderConfirmation } from './_interfaces_/order-confirmation.interface';
import { OrderDetails } from './_interfaces_/order-details.interface';
import { OrderSummary } from './_interfaces_/order-summary.interface';
import { OrderMapped } from './_interfaces_/order.mapped.interface';
import { Orders, SearchOrdersPayload } from './_interfaces_/orders.interface';
import { TicketConditions } from './_interfaces_/ticket-conditions.interface';
import { TicketConditionsMapped } from './_interfaces_/ticket-conditions.mapped.interface';
import { OrderMapperService } from './order-mapper.service';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  constructor(
    @Inject(HttpClient) private _http: HttpClient,
    @Inject(UrlService) private _urlService: UrlService,
    @Inject(Router) private _router: Router,
    @Inject(OrderMapperService) private _mapper: OrderMapperService,
    @Inject(LocalStorageService) private _localStorageService: LocalStorageService
  ) {}

  searchOrders(body: SearchOrdersPayload): Observable<OrderMapped[]> {
    const sessionId = this._localStorageService.getUserSessionToken();
    const options = { headers: { SessionId: sessionId } };
    const url = this._urlService.urls[UrlEnum.GET_ORDERS];
    return this._http.post<Orders>(url, body, options).pipe(map((orders) => this._mapper.mapOrders(orders)));
  }

  public getBookedOrderDetails(dossierNumber: string, culture: string): Observable<BookedOrderMapped> {
    const url: string = `${this._urlService.urls[UrlEnum.GET_FINAL_BOOKED_ORDER]}?dossierNumber=${dossierNumber}&culture=${culture}`;
    const headers = { Sessionid: this._localStorageService.getUserSessionToken() };
    return this._http.get<BookedOrder>(url, { headers }).pipe(
      map((order) => {
        if (order.errors) {
          this._router.navigate(['404'], { queryParams: { message: order.errors[0].title } });
          throw new HttpErrorResponse({ status: 404, error: { Reason: order.errors[0].title } });
        }
        return order;
      }),
      map((order) => this._mapper.mapBookedOrder(order))
    );
  }

  public getBookedOrderActionInfo(dossierNumber: string): Observable<BookedOrderActionInfo> {
    const url: string = `${this._urlService.urls[UrlEnum.GET_FINAL_ORDER_ACTION_INFO]}?dossierNumber=${dossierNumber}`;
    const headers = { SessionId: this._localStorageService.getUserSessionToken() };
    return this._http.get<BookedOrderActionInfo>(url, { headers });
  }

  public getTicketConditions(ticketConditionsLink: LinksMapped): Observable<TicketConditionsMapped> {
    const url = ticketConditionsLink.url;
    const method = ticketConditionsLink.method;
    return this._http.request<TicketConditions>(method, url).pipe(map((conditions) => this._mapper.mapTicketConditions(conditions)));
  }

  public getOrderDetails(id: string): Observable<OrderDetails> {
    const url = this._urlService.parseUrl(this._urlService.urls[UrlEnum.GET_ORDER_DETAILS], { ORDER_ID: id });
    return this._http.get<OrderDetails>(url);
  }

  public getOrderDetailsSummary(id: string): Observable<OrderSummary> {
    const url = this._urlService.parseUrl(this._urlService.urls[UrlEnum.GET_ORDER_SUMMARY], { ORDER_ID: id });
    return this._http.get<OrderSummary>(url);
  }

  public getOrderDetailsConfirmation(id: string): Observable<OrderConfirmation> {
    const url = this._urlService.parseUrl(this._urlService.urls[UrlEnum.GET_ORDER_CONFIRMATION], { ORDER_ID: id });
    return this._http.get<OrderConfirmation>(url);
  }

  public postDocumentRequest(methodType: string, url: string): Observable<DocumentQueueResponse> {
    return this._http.request<DocumentQueueResponse>(methodType, url, {});
  }
}
